import { type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import {
  BEZUGSNAME,
  PRODUKTARTNAME,
  RANGSTUFENNAME,
  VERTRIEBSFORMNAME,
  type Artikel,
  type Preisauskunft,
  type Preisliste,
  type Preiszeile,
  type Vertriebsform,
  type Wirksam,
  akte as holeAkte,
  feldSichern,
  preisAnlegen,
  preisBeenden,
  preisFortschreiben,
  preisauskunft,
  preise as holePreise,
  preislisten as holePreislisten,
  staffelSetzen,
  standorte as holeStandorte,
  standortEntfernen,
  standortHinzufuegen,
  FAHRZEUGARTNAME,
  UMFANGNAME,
  ZIELGRUPPENNAME,
  type Fahrzeugart,
  type Umfang,
  type Zielgruppe,
  varianteAnlegen,
  voraussetzungenSetzen,
  voraussetzungenLoeschen,
} from '../api/produkte';
import { liste as kundenliste, type Kundenzeile } from '../api/kunden';
import { Trainingsdefinition } from './Trainingsdefinition';
import { Eventangaben } from './Eventangaben';
import { ApiFehler } from '../api/fehler';
import { Gutscheinbetrag } from './Gutscheinbetrag';
import { rechtSetzen } from '../api/widerrufe';
import { Dialog } from '../komponenten/Dialog';
import { AbwaehlbareMarke } from '../komponenten/Filtermarken';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import type { Kopf } from '../komponenten/Rahmen';
import {
  Abschnitt,
  Aktenrumpf,
  Feldraster,
  Feldzeile,
  Kennzahlenleiste,
  Reiterleiste,
} from '../komponenten/Akte';
import { markenklasse } from '../marken';
import { alsTag, alsZeitpunkt } from '../datum';
import { Checkinangaben } from './Checkinangaben';
import { Programmvorgaben } from './Programmvorgaben';
import { Zusatzangebote, Zusatzpool } from './Zusatzleistungen';
import { Warteliste } from './Warteliste';
import { KUNDENARTEN, KUNDENARTNAME } from '../api/kunden';
import { alsEuro, alsProzent } from '../betrag';

type Reiter = 'stammdaten' | 'programm' | 'preise' | 'vorschau' | 'verwendung';

/**
 * Die Reiter stehen von Anfang an FEST — auch der, dessen Modul noch fehlt.
 *
 * Ein Reiter, der spaeter auftaucht, verschiebt die Leiste und macht
 * eingeuebte Wege ungueltig. Derselbe Grundsatz wie in der Kundenakte.
 *
 * KEIN ZÄHLER AM REITER (ÄA-41). Die Kundenakte zählt, was sie ohnehin
 * geladen hat. Hier kämen die Preise erst mit ihrem Reiter; ein Zähler, der
 * beim ersten Klick erscheint, sagte vorher nichts und danach, was man sieht.
 */
const REITER: readonly { kennung: Reiter; beschriftung: string }[] = [
  { kennung: 'stammdaten', beschriftung: 'Stammdaten' },
  { kennung: 'programm', beschriftung: 'Programm' },
  { kennung: 'preise', beschriftung: 'Preise' },
  { kennung: 'vorschau', beschriftung: 'Preisvorschau' },
  { kennung: 'verwendung', beschriftung: 'Verwendung' },
];

/**
 * Der Geltungsbereich einer Kondition, ausgeschrieben (0028).
 *
 * "alle" allein stuende ohne Bezugswort da; "für alle Artikel" sagt, worauf
 * es sich bezieht.
 */
const BEREICHSNAME: Readonly<Record<'artikel' | 'warengruppe' | 'alle', string>> = {
  artikel: 'dieser Artikel',
  warengruppe: 'Warengruppe',
  alle: 'alle Artikel',
};

/**
 * Der Tag vor einem ISO-Datum.
 *
 * `new Date('2027-01-01')` liest als UTC-Mitternacht; in einer Zeitzone
 * oestlich davon faellt der Vortag sonst um zwei Tage zurueck. Gerechnet
 * wird deshalb auf den Bestandteilen und nicht auf der Zeitachse.
 */
function tagDavor(iso: string): string {
  const [jahr, monat, tag] = iso.split('-').map(Number);
  if (jahr === undefined || monat === undefined || tag === undefined) return iso;
  const d = new Date(jahr, monat - 1, tag - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Der heutige Tag als ISO-Datum, ohne Zeitzonensprung. */
function heute(): string {
  const j = new Date();
  return `${j.getFullYear()}-${String(j.getMonth() + 1).padStart(2, '0')}-${String(j.getDate()).padStart(2, '0')}`;
}

/**
 * Ein vererbbarer Wert (PRD-02, PRD-31) — was in der Feldzeile steht.
 *
 * VIER ZUSTAENDE, die ohne diese Darstellung gleich aussehen: am Artikel
 * gesetzt, vom Stammprodukt geerbt, von einer Gruppe geerbt, nirgends
 * gesetzt. Der geerbte Wert steht mit seiner Herkunft da und nicht als
 * Vorbelegung eines Feldes — sonst traegt der Anwender ein, was er schon
 * hat, und die Vererbung ist damit gebrochen.
 */
function geerbt(wirksam: Wirksam): ReactNode {
  return (
    <>
      {wirksam.wert ?? '— nicht gesetzt'}
      {wirksam.herkunft === 'warengruppe' && (
        <span className="kanalliste__name"> · geerbt von {wirksam.gruppenname}</span>
      )}
      {/* PRD-31, AA-008 Punkt 13: Die Variante folgt ihrem Stammprodukt —
          und der Name sagt, welchem. Ohne ihn saehe der geerbte Wert aus
          wie ein gepflegter. */}
      {wirksam.herkunft === 'stammprodukt' && (
        <span className="kanalliste__name"> · vom Stammprodukt {wirksam.gruppenname}</span>
      )}
      {wirksam.herkunft === 'artikel' && (
        <span className="kanalliste__name"> · am Artikel gesetzt</span>
      )}
    </>
  );
}

/**
 * Eine Zeile des Feldrasters, deren Wert eine AUSWAHL ist, die sofort sichert
 * (Standort, Widerrufsrecht).
 *
 * Die Feldzeile mit Stift öffnet erst ein Feld; eine Auswahl aus drei Werten
 * ist aber schon das Feld. Sie steht deshalb gleich als Auswahl in der
 * Zeile — beschriftet wie jede andere, gesperrt ohne Recht.
 */
function Wahlzeile({
  beschriftung,
  kennung,
  children,
}: {
  readonly beschriftung: string;
  readonly kennung: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div className="feldzeile">
      <div className="feldzeile__inhalt">
        <label className="feldzeile__beschriftung" htmlFor={kennung}>
          {beschriftung}
        </label>
        {children}
      </div>
    </div>
  );
}

/**
 * Die Artikelakte (Lastenheft Kapitel 5).
 *
 * SEITENTYP AKTE (ÄA-41, Entwurf v2): Aktenkopf mit Sinnbild, „Produktart ·
 * Nummer", der Bezeichnung als Titel und der Kennzahlenleiste; links die
 * Reiter, rechts die Seitenleiste mit Status und Fassung. Stammdaten sichern
 * feldweise — der Stift öffnet ein Feld, es gibt keinen Speichern-Knopf.
 *
 * DER REITER „PREISVORSCHAU" IST DER EIGENTLICHE PUNKT DIESER MASKE. Er zeigt
 * dieselbe Kette, die spaeter Angebot, Auftrag und Rechnung rechnet, mit
 * aufgeklappter Herleitung je Stufe (PRF-13). Wer wissen will, warum ein
 * Kunde 199 statt 249 zahlt, sieht hier die Kandidaten nebeneinander — und
 * was die Auskunft NICHT beruecksichtigt.
 */
export function Artikelakte({
  id,
  darfKonfigurieren = false,
  darfAendern = false,
  darfWartelisteSehen = false,
  darfWartelisteFuehren = false,
  beiKopf,
  beiListe,
  beiOeffnen,
}: {
  readonly id: string;
  /**
   * SYS-10: Preislisten, Preise und Staffeln haengen an
   * `artikel/konfigurieren`. Ohne das Recht zeigt die Preistafel die Preise
   * und BIETET KEINE HANDLUNGEN AN — ein Knopf, der beim Druecken 403
   * liefert, ist schlimmer als keiner: Er behauptet eine Moeglichkeit.
   */
  readonly darfKonfigurieren?: boolean;
  /**
   * SYS-10, zweite Haelfte: Varianten, Teilnahmevoraussetzungen und
   * Verpflegungsangaben beschreiben, WAS verkauft wird, und nicht, was es
   * kostet. Sie haengen deshalb an `artikel/aendern` — dieselbe Trennung wie
   * am Server. Ebenso der Stift an den Stammdaten: `PATCH /produkte/:id`
   * verlangt dasselbe Recht.
   */
  readonly darfAendern?: boolean;
  /** BUC-11 (ÄA-55): die Warteliste des Trainings sehen — `buchung/sehen`. */
  readonly darfWartelisteSehen?: boolean;
  /** BUC-11 (ÄA-55): eintragen und löschen — `buchung/anlegen`. */
  readonly darfWartelisteFuehren?: boolean;
  readonly beiKopf: (kopf: Kopf) => void;
  readonly beiListe: () => void;
  /** Eine andere Akte oeffnen — fuer den Sprung zwischen Stamm und Variante. */
  readonly beiOeffnen?: (id: string) => void;
}): JSX.Element {
  /*
   * PRD-29: Die Trainingsdefinition tritt an die Stelle der Reiter, sie steht
   * nicht darin. Sie ist eine Liste mit eigener Ordnung und eigenem
   * Speichern; unter sichernden Einzelfeldern stuenden zwei Speicherbegriffe
   * nebeneinander. Kopf und Seitenleiste bleiben — man bleibt in der Akte.
   */
  const [definitionOffen, setzeDefinitionOffen] = useState(false);
  /*
   * PRD-33: Die Eventangaben treten wie die Trainingsdefinition AN DIE STELLE
   * der Reiter. Sie tragen eine Liste mit eigenem Speichern; zwei
   * Speicherbegriffe auf einem Bildschirm sind eine Falle.
   */
  const [eventOffen, setzeEventOffen] = useState(false);
  /* TAG-07: die Anordnung — sie steht neben der Definition, nicht darin. */
  const [artikel, setzeArtikel] = useState<Artikel | undefined>(undefined);
  const [reiter, setzeReiter] = useState<Reiter>('stammdaten');
  const [preise, setzePreise] = useState<readonly Preiszeile[]>([]);
  const [listen, setzeListen] = useState<readonly Preisliste[]>([]);
  const [auskunft, setzeAuskunft] = useState<Preisauskunft | undefined>(undefined);
  const [menge, setzeMenge] = useState('1');
  /*
   * Die Vertriebsform der Vorschau. Sie wird auf die ERSTE Form des Artikels
   * gesetzt, sobald er geladen ist: Vorher stand hier fest 'einzelplatz',
   * waehrend das Auswahlfeld nur die Formen des Artikels anbot — die
   * Vorschau fragte dann eine andere Form ab, als der Anwender sah, und bei
   * einem reinen Exklusivprodukt lieferte sie "kein Preis".
   */
  const [form, setzeForm] = useState<Vertriebsform | undefined>(undefined);
  /*
   * DER KUNDE DER VORSCHAU (Stufe 3, PRD-15).
   *
   * Ohne ihn ist die Auskunft der Listenpreis, und das steht so im
   * Vorbehalt. Mit ihm greift die Kundenkondition — und genau das ist die
   * Frage, die im Verkaufsgespraech gestellt wird: "Was kostet das FUER
   * DIESEN Kunden?"
   */
  const [kunde, setzeKunde] = useState<{ readonly id: string; readonly name: string } | null>(null);
  const [kundensuche, setzeKundensuche] = useState('');
  const [kundentreffer, setzeKundentreffer] = useState<readonly Kundenzeile[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [standorte, setzeStandorte] = useState<
    readonly { readonly kennung: string; readonly bezeichnung: string }[]
  >([]);
  /* Der Standort, der mit „Hinzufügen" ans Produkt geht (Auftrag #22). */
  const [neuerStandort, setzeNeuerStandort] = useState('');
  /*
   * Im Ref: Der Kopf wird in `laden` gesetzt, und `laden` soll nicht bei
   * jedem Bild neu entstehen — ein neuer Rückruf als Abhängigkeit setzte den
   * Kopf ohne Ende neu.
   */
  const zurListe = useRef(beiListe);
  zurListe.current = beiListe;

  // PRD-01 i. d. F. AeA-39 (Entwurf): nur Produkte mit Terminen tragen Standorte.
  const traegtTermine = artikel?.produktart === 'fahrtraining' || artikel?.produktart === 'event';
  useEffect(() => {
    if (!traegtTermine) return;
    let verworfen = false;
    holeStandorte()
      .then((a) => {
        if (!verworfen) setzeStandorte(a.standorte);
      })
      .catch(() => {
        if (!verworfen) setzeStandorte([]);
      });
    return () => {
      verworfen = true;
    };
  }, [traegtTermine]);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const geladen = await holeAkte(id);
      setzeArtikel(geladen);
      setzeForm((bisher) =>
        bisher !== undefined && geladen.vertriebsformen.includes(bisher)
          ? bisher
          : geladen.vertriebsformen[0],
      );
      /*
       * DER AKTENKOPF (ÄA-41): Sinnbild, „Produktart · Nummer", die
       * Bezeichnung als Titel; darunter die Kennzahlen, die man beim Öffnen
       * wissen will, ohne einen Reiter zu wählen.
       */
      const zeile = [
        geladen.warengruppenname,
        geladen.vertriebsformen.map((f) => VERTRIEBSFORMNAME[f]).join(', '),
      ]
        .filter((teil): teil is string => teil !== null && teil !== '')
        .join(' · ');
      beiKopf({
        kicker: `${PRODUKTARTNAME[geladen.produktart]} · ${geladen.artikelnummer}`,
        titel: geladen.bezeichnung,
        // Die kurze Zeile unter dem Titel: wohin es gehört und wie es verkauft wird.
        unterzeile: zeile === '' ? undefined : zeile,
        sinnbild: 'tag',
        // Der Zustand steht im Kopf und nicht nur in der Seitenleiste: Wer die
        // Akte oeffnet, soll sofort sehen, ob der Artikel noch waehlbar ist
        // (PRD-03).
        kennzeichen:
          geladen.status === 'aktiv' && !geladen.geloescht ? undefined : (
            <span className="marke marke--gesperrt">
              {geladen.geloescht ? 'im Papierkorb' : geladen.status}
            </span>
          ),
        kennzahlen: (
          <Kennzahlenleiste
            zahlen={[
              { beschriftung: 'Produktart', wert: PRODUKTARTNAME[geladen.produktart] },
              { beschriftung: 'Warengruppe', wert: geladen.warengruppenname ?? '—' },
              { beschriftung: 'Mengeneinheit', wert: geladen.mengeneinheit },
              // PRD-02: der wirksame Schlüssel, auch wenn er geerbt ist.
              {
                beschriftung: 'Steuerschlüssel',
                wert: geladen.wirksam.steuerschluessel.wert ?? '— nicht gesetzt',
              },
              // DAT-22
              {
                beschriftung: 'Fassung',
                wert: geladen.fassung === null ? '—' : `Fassung ${String(geladen.fassung.nummer)}`,
              },
              { beschriftung: 'Angelegt', wert: alsTag(geladen.angelegtAm) ?? '—' },
            ]}
          />
        ),
        handlungen: (
          <button type="button" className="knopf" onClick={() => zurListe.current()}>
            Zur Liste
          </button>
        ),
      });
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, [id, beiKopf]);

  useEffect(() => {
    void laden();
  }, [laden]);

  useEffect(() => {
    if (reiter !== 'preise') return;
    // Ein verschluckter Ladefehler zeigte "Noch kein Preis" — eine
    // Tatsachenaussage ueber einen Zustand, den niemand geprueft hat.
    void holePreise(id, true)
      .then(setzePreise)
      .catch((u: unknown) => setzeFehler(u as ApiFehler));
    void holePreislisten()
      .then(setzeListen)
      .catch((u: unknown) => setzeFehler(u as ApiFehler));
  }, [reiter, id]);

  useEffect(() => {
    if (reiter !== 'vorschau' || form === undefined) return;
    void preisauskunft(id, {
      menge: Number(menge) || 1,
      vertriebsform: form,
      kunde: kunde?.id ?? null,
    })
      .then(setzeAuskunft)
      .catch((u: unknown) => setzeFehler(u as ApiFehler));
  }, [reiter, id, menge, form, kunde]);

  /*
   * Die Kundensuche der Vorschau, mit einer Verzoegerung von 250 ms.
   *
   * OHNE SIE lief eine Abfrage je Tastendruck — bei acht Zeichen also acht,
   * von denen sieben verworfen werden. Die Suche laeuft ueber pg_trgm gegen
   * 50.000 Kunden (KUN-22); das ist nicht teuer, aber es ist auch nicht
   * umsonst. Der Abbruch beim Aufraeumen verhindert zugleich, dass eine
   * spaet eintreffende aeltere Antwort eine neuere ueberschreibt.
   */
  useEffect(() => {
    if (reiter !== 'vorschau' || kundensuche.trim().length < 2) {
      setzeKundentreffer([]);
      return;
    }
    let verworfen = false;
    const uhr = setTimeout(() => {
      void kundenliste({ suche: kundensuche.trim() })
        .then((e) => {
          if (!verworfen) setzeKundentreffer(e.kunden.slice(0, 8));
        })
        .catch(() => {
          if (!verworfen) setzeKundentreffer([]);
        });
    }, 250);
    return () => {
      verworfen = true;
      clearTimeout(uhr);
    };
  }, [reiter, kundensuche]);

  /**
   * Feldweises Sichern (M-12 sinngemaess).
   *
   * Nach dem Sichern wird die Akte NEU geholt und nicht die vorhandene
   * fortgeschrieben: Der wirksame Wert kann sich mit der Warengruppe
   * geaendert haben (PRD-02), und ein fortgeschriebener Zustand zeigte den
   * alten.
   */
  /**
   * Einen Standort ans Produkt nehmen oder davon wegnehmen (PRD-01 i. d. F.
   * AeA-39, Entwurf, Auftrag #22). Ohne Stand — die Zuordnung ist eine
   * Menge —, danach wird die Akte neu geholt wie beim feldweisen Sichern.
   */
  async function standortAendern(was: 'hinzu' | 'weg', kennung: string): Promise<void> {
    if (artikel === undefined) return;
    try {
      if (was === 'hinzu') await standortHinzufuegen(artikel.id, kennung);
      else await standortEntfernen(artikel.id, kennung);
      setzeFehler(undefined);
      setzeRueckmeldung(was === 'hinzu' ? 'Standort hinzugefügt.' : 'Standort entfernt.');
      setzeNeuerStandort('');
      await laden();
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  async function sichern(
    feld: string,
    wert: string | boolean | null | readonly string[],
  ): Promise<void> {
    if (artikel === undefined) return;
    try {
      await feldSichern(artikel.id, feld, wert, artikel.stand);
      setzeRueckmeldung('Gesichert.');
      await laden();
    } catch (ursache) {
      // Der Konflikt nach ERR-13 bleibt STEHEN — aber der Stand muss
      // nachgezogen werden. Sonst schickt jeder weitere Versuch denselben
      // veralteten Stand, und der Anwender kommt nie wieder aus dem Konflikt
      // heraus. Nachgeholt wird deshalb der Datensatz, nicht die Meldung.
      setzeFehler(ursache as ApiFehler);
      try {
        setzeArtikel(await holeAkte(artikel.id));
      } catch {
        // Ein zweiter Fehler beim Nachladen darf die erste Meldung nicht
        // ersetzen — sie ist die, die der Anwender lesen muss.
      }
    }
  }

  if (fehler !== undefined && artikel === undefined) {
    return (
      <>
        <Meldung fehler={fehler} />
        <button type="button" className="knopf" onClick={beiListe}>
          Zurück zur Liste
        </button>
      </>
    );
  }
  if (artikel === undefined) return <p role="status">Wird geladen …</p>;

  /* Ohne Recht kein Stift — die Zeile ist dann nur zu lesen (SYS-10). */
  const sicherbar = (feld: string): ((wert: string | null) => Promise<void>) | undefined =>
    darfAendern ? (wert) => sichern(feld, wert) : undefined;

  /*
   * DIE SEITENLEISTE: das Zeitliche des Artikels — sein Zustand, die
   * geltende Fassung und seit wann es ihn gibt. Sie steht auch neben der
   * Trainingsdefinition und den Eventangaben: Wer dort eine neue Fassung
   * erzeugt, sieht sie hier nach dem Speichern.
   */
  const seite = (
    <>
      <div className="akte__seitentitel">
        <span>Status und Fassung</span>
      </div>

      <section className="seitenblock">
        <h2 className="seitenblock__titel">
          Status
        </h2>
        <p>
          {/* WCAG 1.4.1: Der Zustand steht als Text da, die Farbe kommt hinzu. */}
          <span className={markenklasse(artikel.status === 'aktiv' ? 'laufend' : 'stillgelegt')}>
            {artikel.status}
          </span>
          {artikel.geloescht && <span className="marke marke--gesperrt">im Papierkorb</span>}
        </p>
        {/* PRD-03 */}
        <p className="feld__hilfe">
          {artikel.status === 'aktiv' && !artikel.geloescht
            ? 'In neuen Angeboten, Aufträgen und Buchungen wählbar.'
            : 'Belege, die ihn schon tragen, zeigen ihn weiter.'}
        </p>
      </section>

      {/*
        DAT-22: DIE FASSUNG STEHT DABEI, WEIL SIE ETWAS ZUSICHERT.

        Nicht als Zierde: Ein Termin behält die Fassung, mit der er
        angelegt wurde. Wer den Inhalt ändert, sieht hier, dass daraus
        eine neue Fassung wird — und dass die laufenden Termine ihre
        behalten. Ohne diese Angabe sähe eine Inhaltsänderung aus wie
        jede andere Feldänderung.

        Der Block trägt die Überschrift, aber keinen Namen über
        `aria-labelledby`: Ein Element namens „Fassung" hielte eine
        Vorlesehilfe für ein Feld, das man setzen kann — die Fassung entsteht
        aus dem Inhalt.
      */}
      {artikel.fassung !== null && (
        <section className="seitenblock">
          <h2 className="seitenblock__titel">
            Fassung
          </h2>
          <p>
            <strong>Fassung {artikel.fassung.nummer}</strong>, gültig seit{' '}
            {alsZeitpunkt(artikel.fassung.gueltigAb)}
          </p>
          {/* PRD-37 */}
          <p className="feld__hilfe">
            Eine inhaltliche Änderung — Bausteine, Dauer, Programmvorgaben — erzeugt
            eine neue Fassung. Termine behalten die Fassung, mit der sie
            angelegt wurden; das gilt auch für Bescheinigung und Nachweis.
            Preis und Vertriebsform gehören nicht dazu.
          </p>
        </section>
      )}

      <section className="seitenblock">
        <h2 className="seitenblock__titel">
          Angelegt
        </h2>
        <p>{alsZeitpunkt(artikel.angelegtAm) ?? '—'}</p>
      </section>
    </>
  );

  /*
   * PRD-29: Die Trainingsdefinition tritt AN DIE STELLE der Reiter.
   *
   * Sie ist eine geordnete Liste mit einem eigenen Speichern; die Akte
   * sichert jedes Feld fuer sich. Beides untereinander haette zwei
   * Speicherbegriffe auf einem Bildschirm — und ein "Speichern"-Knopf, der
   * nur die halbe Maske meint, ist eine Falle.
   */
  if (definitionOffen) {
    return (
      <Aktenrumpf
        haupt={
          <Trainingsdefinition
            artikel={artikel.id}
            darfKonfigurieren={darfKonfigurieren}
            beiSchliessen={() => {
              setzeDefinitionOffen(false);
              void laden();
            }}
          />
        }
        seite={seite}
      />
    );
  }

  if (eventOffen) {
    return (
      <Aktenrumpf
        haupt={
          <Eventangaben
            artikel={artikel.id}
            darfAendern={darfAendern}
            beiSchliessen={() => {
              setzeEventOffen(false);
              void laden();
            }}
          />
        }
        seite={seite}
      />
    );
  }

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      <p role="status" className="nur-vorlesen">
        {rueckmeldung}
      </p>

      {/* DAT-25: stillgelegt statt geloescht — Belege zeigen weiter auf ihn. */}
      {artikel.geloescht && (
        <p className="warnung">
          Dieser Artikel liegt im Papierkorb. Er bleibt lesbar, weil Belege auf ihn zeigen.
        </p>
      )}
      {artikel.status === 'gesperrt' && !artikel.geloescht && (
        <p className="warnung">
          Gesperrt: in neuen Belegen nicht wählbar, in alten sichtbar.
        </p>
      )}

      <Aktenrumpf
        haupt={
          <>
            <Reiterleiste
              beschriftung="Bereiche der Artikelakte"
              gewaehlt={reiter}
              beiWahl={setzeReiter}
              reiter={REITER}
            />

            <div role="tabpanel" id={`tafel-${reiter}`} aria-labelledby={`reiter-${reiter}`}>
              {reiter === 'stammdaten' && (
                <div className="tafel__abschnitte">
                  <Abschnitt titel="Beschreibung">
                    <Feldraster>
                      <Feldzeile
                        beschriftung="Bezeichnung"
                        kennung="artikel-bezeichnung"
                        wert={artikel.bezeichnung}
                        {...optional(sicherbar('bezeichnung'))}
                      />
                      <Feldzeile
                        beschriftung="Kurztext"
                        kennung="artikel-kurztext"
                        wert={artikel.kurztext}
                        hilfe="Erscheint auf dem Beleg."
                        {...optional(sicherbar('kurztext'))}
                      />
                      <Feldzeile
                        beschriftung="Langtext"
                        kennung="artikel-langtext"
                        wert={artikel.langtext}
                        {...optional(sicherbar('langtext'))}
                      />
                    </Feldraster>
                    {darfAendern && (
                      <p className="hilfe">
                        Der Stift öffnet ein Feld; es wird beim Verlassen gesichert. Es gibt
                        keinen Speichern-Knopf.
                      </p>
                    )}
                  </Abschnitt>

                  <Abschnitt titel="Einordnung" zusatz={PRODUKTARTNAME[artikel.produktart]}>
                    <Feldraster>
                      <Feldzeile beschriftung="Artikelnummer" wert={artikel.artikelnummer} />
                      <Feldzeile
                        beschriftung="Produktart"
                        wert={PRODUKTARTNAME[artikel.produktart]}
                      />
                      <Feldzeile beschriftung="Warengruppe" wert={artikel.warengruppenname} />
                      <Feldzeile beschriftung="Mengeneinheit" wert={artikel.mengeneinheit} />
                      {/* PRD-18, SYS-03: Das Feld FEHLT, wenn das Recht fehlt — es
                          steht nicht grau da. */}
                      {'einkaufspreis' in artikel && (
                        <Feldzeile
                          beschriftung="Einkaufspreis"
                          wert={artikel.einkaufspreis ?? null}
                          anzeige={artikel.einkaufspreis === null || artikel.einkaufspreis === undefined ? '— nicht gesetzt' : alsEuro(artikel.einkaufspreis)}
                        />
                      )}
                    </Feldraster>
                  </Abschnitt>

                  <Abschnitt titel="Vertrieb">
                    <Feldraster>
                      <Feldzeile
                        beschriftung="Vertriebsformen"
                        wert={null}
                        anzeige={
                          <>
                            {artikel.vertriebsformen.map((f) => VERTRIEBSFORMNAME[f]).join(', ')}
                            {/* BUC-84: der Bezug IST die Preisstellung der
                                Zusatzleistung (PRD-42) und gehört deshalb daneben. */}
                            {artikel.bezugZusatzleistung !== null && (
                              <span className="kanalliste__name">
                                {' '}
                                · {BEZUGSNAME[artikel.bezugZusatzleistung]}
                              </span>
                            )}
                          </>
                        }
                      />
                      {/* LEG-16: ob fuer Verbraucher ein Widerrufsrecht besteht (C04 klaert es je Produktart). */}
                      <Wahlzeile beschriftung="Widerrufsrecht" kennung="artikel-widerrufsrecht">
                        <select
                          id="artikel-widerrufsrecht"
                          className="feld__eingabe"
                          disabled={!darfAendern}
                          value={artikel.widerrufsrecht ?? ''}
                          onChange={(e) => {
                            const wert =
                              e.target.value === ''
                                ? null
                                : (e.target.value as 'besteht' | 'besteht_nicht');
                            void rechtSetzen('artikel', artikel.id, wert)
                              .then(laden)
                              .catch((u: unknown) => setzeFehler(u as ApiFehler));
                          }}
                        >
                          <option value="">noch nicht geklärt</option>
                          <option value="besteht">besteht für Verbraucher</option>
                          <option value="besteht_nicht">besteht nicht</option>
                        </select>
                      </Wahlzeile>
                    </Feldraster>
                    {/*
                      BUC-105: Wem der Artikel angeboten wird. Steht bei den
                      Vertriebsformen, weil beides dieselbe Frage beantwortet —
                      die eine "auf welchem Weg", die andere "an wen".
                    */}
                    <div className="feld">
                      <span className="feld__beschriftung">Angeboten an</span>
                      <fieldset className="wahlgruppe">
                        <legend className="feld__hilfe">Wem dieser Artikel angeboten wird</legend>
                        {KUNDENARTEN.map((a) => {
                          const drin = artikel.angebotenAn.includes(a);
                          // Der letzte Haken laesst sich nicht loesen: Ein
                          // Artikel, den niemand buchen darf, wird gesperrt.
                          const letzter = drin && artikel.angebotenAn.length === 1;
                          return (
                            <label key={a} className="wahlreihe">
                              <input
                                type="checkbox"
                                checked={drin}
                                disabled={!darfAendern || letzter}
                                onChange={(e) => {
                                  void sichern(
                                    'angebotenAn',
                                    e.target.checked
                                      ? [...artikel.angebotenAn, a]
                                      : artikel.angebotenAn.filter((x) => x !== a),
                                  );
                                }}
                              />
                              {KUNDENARTNAME[a]}
                            </label>
                          );
                        })}
                        {artikel.angebotenAn.length === 1 && darfAendern && (
                          <p className="feld__hilfe">
                            Die letzte Kundenart bleibt stehen. Soll der Artikel niemandem mehr
                            offenstehen, setzen Sie ihn auf „gesperrt".
                          </p>
                        )}
                      </fieldset>
                    </div>
                  </Abschnitt>

                  {/* PRD-02: Der geerbte Wert steht mit seiner Herkunft da. */}
                  <Abschnitt titel="Steuer und Konten" zusatz="von der Warengruppe geerbt, wo leer">
                    <Feldraster>
                      <Feldzeile
                        beschriftung="Steuerschlüssel"
                        wert={artikel.wirksam.steuerschluessel.wert}
                        anzeige={geerbt(artikel.wirksam.steuerschluessel)}
                      />
                      <Feldzeile
                        beschriftung="Erlöskonto Inland"
                        wert={artikel.wirksam.erloeskontoInland.wert}
                        anzeige={geerbt(artikel.wirksam.erloeskontoInland)}
                      />
                      <Feldzeile
                        beschriftung="Erlöskonto EU"
                        wert={artikel.wirksam.erloeskontoEu.wert}
                        anzeige={geerbt(artikel.wirksam.erloeskontoEu)}
                      />
                      {/* PRF-11: Der Höchstrabatt erbt wie der Steuerschlüssel. */}
                      <Feldzeile
                        beschriftung="Höchstrabatt %"
                        wert={artikel.wirksam.hoechstrabattProzent.wert}
                        anzeige={geerbt(artikel.wirksam.hoechstrabattProzent)}
                      />
                    </Feldraster>
                  </Abschnitt>

                  {/*
                    TABELLE 27, ZEILE 2 (0252): der Verkaufsstart am Produkt. Leer
                    heisst: Termine werden nur von Hand freigegeben.
                  */}
                  {traegtTermine && (
                    <Abschnitt titel="Termine">
                      <Feldraster>
                        <Feldzeile
                          beschriftung="Verkaufsstart (Tage vor Beginn)"
                          kennung="artikel-verkaufsstartTage"
                          wert={
                            artikel.verkaufsstartTage === null
                              ? null
                              : String(artikel.verkaufsstartTage)
                          }
                          anzeige={
                            artikel.verkaufsstartTage === null
                              ? 'nur durch die Freigabe am Termin'
                              : `${String(artikel.verkaufsstartTage)} Tage vor Beginn`
                          }
                          hilfe="So viele Tage vor Beginn wird ein geplanter Termin dieses Produkts von selbst buchbar. Leer: nur durch die Freigabe am Termin."
                          {...optional(sicherbar('verkaufsstartTage'))}
                        />
                      </Feldraster>
                      {/*
                        PRD-01, BUC-01 i. d. F. AeA-39 (Entwurf, Auftrag #22): DIE
                        STANDORTE DES PRODUKTS, null bis beliebig viele.

                        Als Marken, wie der Entwurf v2 eine Menge zeigt; jede Marke
                        nimmt ihren Standort mit einem Klick weg. Hinzu kommt einer
                        über die Auswahl und „Hinzufügen" — ein Knopf und kein
                        Sichern beim Wählen: Mit den Pfeiltasten wandert eine
                        Auswahl über jeden Eintrag, und jeder davon landete sonst
                        am Produkt (NFA-07).

                        Wählbar sind nur aktive Standorte (RES-01). Ein inzwischen
                        abgeschalteter bleibt als Marke stehen, bis jemand ihn
                        entfernt, und sagt, dass er abgeschaltet ist.
                      */}
                      <div className="produktstandorte">
                        <p className="feldzeile__beschriftung" id="artikel-standorte">
                          Standorte
                        </p>
                        {artikel.standorte.length === 0 ? (
                          <p className="hilfe">Kein Standort.</p>
                        ) : (
                          <ul className="liste__marken" aria-labelledby="artikel-standorte">
                            {artikel.standorte.map((s) => {
                              const name = s.aktiv ? s.bezeichnung : `${s.bezeichnung} (abgeschaltet)`;
                              return (
                                <li key={s.kennung}>
                                  {darfAendern ? (
                                    <AbwaehlbareMarke
                                      text={name}
                                      inaktiv={!s.aktiv}
                                      titel={`${s.bezeichnung} vom Produkt entfernen`}
                                      entfernen={() => {
                                        void standortAendern('weg', s.kennung);
                                      }}
                                    />
                                  ) : (
                                    <span className={`marke${s.aktiv ? '' : ' marke--inaktiv'}`}>{name}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        {darfAendern && (
                          <div className="produktstandorte__hinzu">
                            <label className="nur-vorlesen" htmlFor="artikel-standort-neu">
                              Standort hinzufügen
                            </label>
                            <select
                              id="artikel-standort-neu"
                              className="feld__eingabe"
                              value={neuerStandort}
                              onChange={(e) => {
                                setzeNeuerStandort(e.target.value);
                              }}
                            >
                              <option value="">Standort wählen …</option>
                              {standorte
                                .filter((s) => !artikel.standorte.some((z) => z.kennung === s.kennung))
                                .map((s) => (
                                  <option key={s.kennung} value={s.kennung}>
                                    {s.bezeichnung}
                                  </option>
                                ))}
                            </select>
                            <button
                              type="button"
                              className="knopf"
                              disabled={neuerStandort === ''}
                              onClick={() => {
                                void standortAendern('hinzu', neuerStandort);
                              }}
                            >
                              Hinzufügen
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="hilfe">
                        Hat das Produkt genau einen Standort, tragen neue Termine ihn von selbst.
                        Bei mehreren bietet die Terminmaske genau diese zur Wahl an. Ein Termin
                        kann einen anderen tragen; bestehende Termine bleiben, wie sie sind.
                      </p>
                    </Abschnitt>
                  )}

                  {(artikel.produktart === 'fahrtraining' || artikel.leistungsmerkmale.length > 0) && (
                    <Abschnitt titel="Inhalt" zusatz="aus den Bausteinen">
                      {/*
                        PRD-39: DIE MERKMALE SIND ABGELEITET UND DESHALB KEIN FELD.

                        Sie kommen aus der Bausteinfolge, die das Operations Management
                        pflegt — hier stehen sie nur. Wäre das ein Eingabefeld, stünde
                        dieselbe Aussage an zwei Stellen, und die zweite wäre beim
                        nächsten Umbau des Trainings falsch: Genau so verspricht heute
                        ein Kurztext ein Abfangen, das die Folge längst nicht mehr
                        enthält.

                        Eine MENGE, keine Reihenfolge: Seit TAG-07 darf die
                        Tagesplanung innerhalb einer Phase tauschen. Deshalb Punkte und
                        keine Nummern.
                      */}
                      {artikel.leistungsmerkmale.length > 0 && (
                        <div className="feld">
                          <span className="feld__beschriftung">Enthält</span>
                          <ul className="merkmalsliste">
                            {artikel.leistungsmerkmale.map((m) => (
                              <li key={m}>{m}</li>
                            ))}
                          </ul>
                          <p className="feld__hilfe">
                            Aus den Bausteinen dieses Trainings — vom Operations Management gepflegt
                            und hier nicht änderbar. Die Reihenfolge am Tag kann davon
                            abweichen; die Bausteine selbst nicht.
                          </p>
                        </div>
                      )}

                      {/*
                        DER EINSTIEG STEHT AUCH DA, WO ES NOCH NICHTS GIBT.

                        Ein Knopf, der nur neben einer gefüllten Liste erscheint, ist
                        für den unbrauchbar, der die Liste erst anlegen will — und
                        genau das ist der Fall, der auffallen muss: 36 Trainings haben
                        eine Definition, eines nicht.
                      */}
                      {artikel.produktart === 'fahrtraining' && (
                        <div className="feld">
                          <button
                            type="button"
                            className="knopf"
                            onClick={() => {
                              setzeDefinitionOffen(true);
                            }}
                          >
                            Trainingsdefinition{darfKonfigurieren ? ' bearbeiten' : ' ansehen'}
                          </button>
                          {/*
                            TAG-07 i.d.F. AeA-11: Die Definition IST die Anordnung.
                            Bis zum 08.09.2026 stand daneben ein Knopf „Ablaufschemata"
                            — eine zweite Pflege derselben Folge. Der Ablauf folgt jetzt
                            aus den Bausteinen und ihren Phasen; angewendet wird er in
                            der Trainingsplanung, an dem Tag, für den er gilt.
                          */}
                          {artikel.leistungsmerkmale.length === 0 && (
                            <p className="feld__hilfe">
                              Für dieses Training ist noch keine Bausteinfolge hinterlegt. Ohne sie
                              gibt es keine Leistungsmerkmale für den Vertrieb — und keinen Ablauf,
                              den die Tagesplanung anordnen könnte.
                            </p>
                          )}
                        </div>
                      )}
                    </Abschnitt>
                  )}

                  {/*
                    PRD-33: Ort, Rahmen und zugekaufte Leistungen — nur am Event.

                    Der Knopf steht an derselben Stelle wie der der
                    Trainingsdefinition: Beide öffnen, was ZUSÄTZLICH zum Stammsatz an
                    dieser Produktart hängt. Ein eigener Reiter dafür verschöbe die
                    Leiste für alle anderen Artikel.

                    Die benötigten Räume stehen dort MIT, aber nur zum Lesen — sie
                    sind Ressourcenbedarf und werden im Reiter „Programm" gepflegt.
                  */}
                  {artikel.produktart === 'event' && (
                    <Abschnitt titel="Event">
                      <div className="feld">
                        <button
                          type="button"
                          className="knopf"
                          onClick={() => {
                            setzeEventOffen(true);
                          }}
                        >
                          Eventangaben{darfAendern ? ' bearbeiten' : ' ansehen'}
                        </button>
                        <p className="feld__hilfe">
                          Ort, Rahmen und was für dieses Event zugekauft wird.
                        </p>
                      </div>
                    </Abschnitt>
                  )}

                  {/* C18 (0274): der Wert eines Gutscheinartikels -- vor dem Buchbarstellen. */}
                  {artikel.produktart === 'gutschein' && (
                    <Gutscheinbetrag
                      artikel={artikel.id}
                      angaben={artikel.gutschein ?? { art: null, betrag: null, von: null, bis: null }}
                      darfAendern={darfAendern}
                      beiGesichert={() => void laden()}
                      beiFehler={setzeFehler}
                    />
                  )}
                </div>
              )}

              {reiter === 'programm' && (
                <div className="tafel__abschnitte">
                  {/*
                    PRD-23 STEHT VOR PRD-32.

                    Die Programmvorgaben belegen Termin und Tagesplan vor —
                    Gruppengröße, Dauer, Trainerqualifikation. Die
                    Teilnahmevoraussetzungen darunter sagen, wer teilnehmen darf. Das
                    eine ist die Vorgabe für den Betrieb, das andere für den Kunden.
                  */}
                  <Programmvorgaben artikel={artikel.id} darfKonfigurieren={darfKonfigurieren} />
                  {/* TAG-55: was der Check-in fuer dieses Produkt zusaetzlich abfragt. */}
                  <Checkinangaben artikel={artikel.id} darfKonfigurieren={darfKonfigurieren} />
                  <Programm
                    artikel={artikel}
                    darfAendern={darfAendern}
                    beiAenderung={() => void laden()}
                    beiFehler={setzeFehler}
                    {...(beiOeffnen === undefined ? {} : { beiOeffnen })}
                  />
                  {/*
                    ÄA-31: Was zu einem Produkt mitgebucht werden kann, steht am
                    Produkt (BUC-84); was hinter einer Zusatzleistung steht, an ihr
                    (BUC-85). Kein eigener Reiter — die Leiste bleibt fest.
                  */}
                  {artikel.vertriebsformen.some((f) => f !== 'zusatzleistung') && (
                    <Zusatzangebote artikel={artikel.id} darfAendern={darfAendern} />
                  )}
                  {artikel.vertriebsformen.includes('zusatzleistung') && (
                    <Zusatzpool zusatz={artikel.id} darfAendern={darfAendern} />
                  )}
                  {/*
                    BUC-11 nach ÄA-55: der Schalter am Produkt. Er hängt an
                    `artikel/aendern` wie die übrigen Felder des Stammsatzes; die
                    Liste darunter an `buchung/sehen`.
                  */}
                  {traegtTermine && (
                    <Abschnitt titel="Buchungsstrecke">
                      <label className="wahlreihe">
                        <input
                          type="checkbox"
                          checked={artikel.warteliste}
                          disabled={!darfAendern || (!artikel.buchbar && !artikel.warteliste)}
                          onChange={(e) => {
                            void sichern('warteliste', e.target.checked);
                          }}
                        />
                        Warteliste anbieten
                      </label>
                      <p className="feld__hilfe">
                        {artikel.buchbar
                          ? 'Interessenten können sich im Portal und über den Innendienst für 4, 8 oder '
                            + '12 Wochen eintragen und werden bei freiem Platz benachrichtigt.'
                          : 'Eine Warteliste gibt es nur für Produkte, die online buchbar sind — '
                            + 'dieses ist es nicht.'}
                      </p>
                    </Abschnitt>
                  )}
                  {/* BUC-11 nach ÄA-55: Gewartet wird auf ein Training, nicht auf einen Termin. */}
                  {traegtTermine && darfWartelisteSehen && (
                    <Warteliste
                      artikel={artikel.id}
                      angeboten={artikel.warteliste && artikel.buchbar}
                      darfFuehren={darfWartelisteFuehren}
                    />
                  )}
                </div>
              )}

              {reiter === 'preise' && (
                <Preistafel
                  artikel={artikel}
                  preise={preise}
                  listen={listen}
                  darfKonfigurieren={darfKonfigurieren}
                  beiAenderung={() => {
                    void holePreise(id, true).then(setzePreise);
                  }}
                  beiFehler={setzeFehler}
                />
              )}

              {reiter === 'vorschau' && (
                <Vorschau
                  auskunft={auskunft}
                  menge={menge}
                  form={form}
                  formen={artikel.vertriebsformen}
                  kunde={kunde}
                  kundensuche={kundensuche}
                  kundentreffer={kundentreffer}
                  beiMenge={setzeMenge}
                  beiForm={setzeForm}
                  beiKundensuche={setzeKundensuche}
                  beiKunde={(gewaehlt) => {
                    setzeKunde(gewaehlt);
                    setzeKundensuche('');
                    setzeKundentreffer([]);
                  }}
                />
              )}

              {reiter === 'verwendung' && (
                <p className="hinweiszeile">
                  Wo dieser Artikel vorkommt — Angebote, Aufträge, Termine. Der Reiter steht schon
                  hier, damit die Leiste sich später nicht verschiebt; er füllt sich mit dem
                  Ausbau von Angeboten und Aufträgen.
                </p>
              )}
            </div>
          </>
        }
        seite={seite}
      />
    </>
  );
}

/**
 * `beiSichern` nur, wo es einen gibt — `exactOptionalPropertyTypes` lässt
 * kein ausdrückliches `undefined` an eine optionale Eigenschaft.
 */
function optional(
  beiSichern: ((wert: string | null) => Promise<void>) | undefined,
): { beiSichern?: (wert: string | null) => Promise<void> } {
  return beiSichern === undefined ? {} : { beiSichern };
}

/**
 * Die Preistafel (PRD-11, PRF-16).
 *
 * ES GIBT KEIN FELD, DAS EINEN BETRAG UEBERSCHREIBT. Ein geltender Preis wird
 * FORTGESCHRIEBEN: Der laufende wird auf den Vortag beendet, der neue beginnt
 * am gewaehlten Tag. Der Grund steht in PRF-16 — ein Preis, der schon gegolten
 * hat, hat Belege erzeugt, und ein geaenderter Betrag machte sie unerklaerlich.
 */
function Preistafel({
  artikel,
  preise,
  listen,
  darfKonfigurieren,
  beiAenderung,
  beiFehler,
}: {
  readonly artikel: Artikel;
  readonly preise: readonly Preiszeile[];
  readonly listen: readonly Preisliste[];
  readonly darfKonfigurieren: boolean;
  readonly beiAenderung: () => void;
  readonly beiFehler: (fehler: ApiFehler) => void;
}): JSX.Element {
  const [neu, setzeNeu] = useState({
    liste: '',
    preis: '',
    ab: heute(),
    /*
     * DAS ENDE IST EIN FELD UND KEIN FESTWERT.
     *
     * Vorher stand hier fest `gueltigBis: null`: Jeder angelegte Preis lief
     * offen, und ein zweiter fuer dieselbe Liste und Vertriebsform prallte
     * am Ausschlussindex ab (PRD-11). Gemessen am 31.08.2026 im Betrieb —
     * die Meldung sagte "Beenden Sie zuerst den laufenden Zeitraum", und die
     * Maske bot keinen Weg, das zu tun.
     */
    bis: '',
    // PRD-42: Jede Vertriebsform hat ihre eigene Preisstellung. Fest auf die
    // erste gesetzt liessen sich fuer die uebrigen nie Preise anlegen.
    form: artikel.vertriebsformen[0] ?? ('einzelplatz' as Vertriebsform),
  });
  const [fortschreiben, setzeFortschreiben] = useState<
    | {
        readonly preis: string;
        readonly zeile: string;
        readonly betrag: string;
        readonly ab: string;
        /**
         * Der letzte Tag des Vorgaengers. VORBELEGT mit dem Tag davor — das
         * ist der Regelfall und laesst keine Luecke —, aber aenderbar. Bis
         * hierher war er fest, und damit war jede Abloesung an den
         * unmittelbaren Anschluss gebunden.
         */
        readonly ende: string;
      }
    | undefined
  >(undefined);
  const [staffel, setzeStaffel] = useState<
    { readonly preis: string; readonly zeile: string; readonly text: string } | undefined
  >(undefined);
  const [beenden, setzeBeenden] = useState<
    { readonly preis: string; readonly zeile: string; readonly bis: string } | undefined
  >(undefined);

  const melde = (u: unknown): void => beiFehler(u as ApiFehler);

  /*
   * Der Preis, der einem neuen im Weg steht (PRD-11).
   *
   * Die Maske kennt die vorhandenen Preise und kann deshalb SAGEN, welcher
   * blockiert — statt den Anwender gegen einen Ausschlussindex laufen zu
   * lassen und ihm hinterher zu raten, "zuerst den laufenden Zeitraum zu
   * beenden". Geprueft wird dieselbe Bedingung wie in der Datenbank: gleiche
   * Liste, gleiche Vertriebsform, ueberlappender Zeitraum.
   */
  const imWeg = preise.find(
    (p) =>
      p.preisliste === neu.liste
      && p.vertriebsform === neu.form
      && (p.gueltigBis === null || p.gueltigBis >= neu.ab)
      && (neu.bis === '' || neu.bis >= p.gueltigAb),
  );

  return (
    <div className="tafel__abschnitte">
      <Abschnitt titel="Preise" zusatz={`${String(preise.length)} Zeilen, auch beendete`}>
        <div className="tafel--weit">
          <table className="liste__tabelle">
            <thead>
              <tr>
                <th scope="col">Preisliste</th>
                <th scope="col">Vertriebsform</th>
                <th scope="col">Einzelpreis</th>
                <th scope="col">gültig ab</th>
                <th scope="col">gültig bis</th>
                <th scope="col">Staffeln</th>
                <th scope="col">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {preise.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    Noch kein Preis. Ohne ihn liefert die Vorschau keinen Betrag — das ist
                    zulässig, etwa für einen Baustein, der nur in einem Paket vorkommt.
                  </td>
                </tr>
              )}
              {preise.map((p) => (
                <tr key={p.id}>
                  <td className="liste__nummer">{p.preislistenkennung}</td>
                  <td>{VERTRIEBSFORMNAME[p.vertriebsform]}</td>
                  <td className="zahl">{alsEuro(p.einzelpreis)}</td>
                  <td className="liste__einzeilig">{alsTag(p.gueltigAb)}</td>
                  {/*
                    Ein Preis ohne eigenes Ende laeuft nur so lange wie seine
                    PREISLISTE. Vorher stand dort "offen", waehrend die
                    Aktionsliste um ihn herum laengst ausgelaufen war und die
                    Vorschau daneben einen anderen Betrag zeigte — zwei
                    Angaben, die sich widersprachen, ohne dass eine falsch war.
                  */}
                  <td>
                    {p.gueltigBis !== null ? (
                      <span className="liste__einzeilig">{alsTag(p.gueltigBis)}</span>
                    ) : (
                      <>
                        offen
                        {endeDerListe(listen, p.preisliste) !== null && (
                          <span className="kanalliste__name">
                            {' '}
                            · Liste bis{' '}
                            <span className="liste__einzeilig">
                              {alsTag(endeDerListe(listen, p.preisliste))}
                            </span>
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td>{p.staffeln.map((s) => `ab ${s.abMenge}: ${alsEuro(s.einzelpreis)}`).join(' · ') || '—'}</td>
                  <td>
                    {darfKonfigurieren ? (
                      <>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() =>
                            setzeFortschreiben({
                              preis: p.id,
                              zeile: `${p.preislistenkennung} · ${VERTRIEBSFORMNAME[p.vertriebsform]} · ${alsEuro(p.einzelpreis)}`,
                              betrag: p.einzelpreis,
                              ab: heute(),
                              ende: tagDavor(heute()),
                            })
                          }
                        >
                          fortschreiben
                        </button>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() =>
                            setzeStaffel({
                              preis: p.id,
                              zeile: `${p.preislistenkennung} · ${alsEuro(p.einzelpreis)}`,
                              text: p.staffeln
                                .map((s) => `${s.abMenge}=${s.einzelpreis}`)
                                .join(', '),
                            })
                          }
                        >
                          Staffel
                        </button>
                        {/*
                          PRD-11: Ein Preis wird BEENDET, nicht geloescht — die
                          Belege, die ihn verwendet haben, behalten ihn. Der
                          Knopf fehlte, waehrend die Fehlermeldung des
                          Ausschlussindex genau dazu aufforderte: "Beenden Sie
                          zuerst den laufenden Zeitraum." Eine Aufforderung
                          ohne Weg ist keine.
                        */}
                        {p.gueltigBis === null && (
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() =>
                              setzeBeenden({
                                preis: p.id,
                                zeile: `${p.preislistenkennung} · ${VERTRIEBSFORMNAME[p.vertriebsform]} · ${alsEuro(p.einzelpreis)}`,
                                bis: heute(),
                              })
                            }
                          >
                            beenden
                          </button>
                        )}
                      </>
                    ) : (
                      <span className="feld__hilfe">nur lesen</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Abschnitt>

      {/*
        DREI HANDGRIFFE AN EINER PREISZEILE — jeder im eigenen Fenster.

        Sie standen bis hierher untereinander unter der Preistabelle. Bei
        einem Artikel mit sechs Preislisten und mehreren Zeiträumen ist das
        weit weg von der Zeile, um die es geht — und welche gemeint war, stand
        nur als Text im Formular. Jetzt steht sie in der Kopfzeile des
        Fensters, und die Tabelle ist beim Ausfüllen nicht im Weg.
      */}
      {beenden !== undefined && (
        <Dialog
          titel="Preis beenden"
          unterzeile={beenden.zeile}
          beiSchliessen={() => setzeBeenden(undefined)}
        >
        <form
          className="kanalzeile"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void preisBeenden(artikel.id, beenden.preis, beenden.bis)
              .then(() => {
                setzeBeenden(undefined);
                beiAenderung();
              })
              .catch(melde);
          }}
        >
          <span className="feld__beschriftung">Beenden zum</span>
          <input
            type="date"
            value={beenden.bis}
            min={heute()}
            onChange={(e) => setzeBeenden({ ...beenden, bis: e.target.value })}
            aria-label="Letzter Gültigkeitstag"
          />
          <span className="feld__hilfe">
            {beenden.zeile} — der Preis gilt an diesem Tag noch. Rückwirkend geht es nicht: Das
            nähme einem abgerechneten Zeitraum seinen Preis.
          </span>
          <span>
            <button type="button" className="knopf" onClick={() => setzeBeenden(undefined)}>
              Abbrechen
            </button>{' '}
            <button type="submit" className="knopf knopf--haupt">
              Beenden
            </button>
          </span>
        </form>
        </Dialog>
      )}

      {fortschreiben !== undefined && (
        <Dialog
          titel="Preis fortschreiben"
          unterzeile={fortschreiben.zeile}
          beiSchliessen={() => setzeFortschreiben(undefined)}
        >
        <form
          className="kanalzeile"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void preisFortschreiben(artikel.id, fortschreiben.preis, {
              einzelpreis: fortschreiben.betrag,
              abDatum: fortschreiben.ab,
              staffelnUebernehmen: false,
              endeVorgaenger: fortschreiben.ende,
            })
              .then(() => {
                setzeFortschreiben(undefined);
                beiAenderung();
              })
              .catch(melde);
          }}
        >
          {/* WELCHE Zeile abgeloest wird, steht dabei. Bei zwei Preislisten
              am selben Artikel war das Formular sonst nicht zuzuordnen. */}
          <span className="feld__beschriftung">Fortschreiben: {fortschreiben.zeile}</span>
          <input
            type="text"
            value={fortschreiben.betrag}
            onChange={(e) => setzeFortschreiben({ ...fortschreiben, betrag: e.target.value })}
            aria-label="Neuer Einzelpreis"
          />
          <input
            type="date"
            value={fortschreiben.ab}
            onChange={(e) => setzeFortschreiben({ ...fortschreiben, ab: e.target.value })}
            // Nicht "Gültig ab": Diesen Namen traegt schon das Feld im
            // Anlegeformular darunter, und zwei gleich benannte
            // Bedienelemente sind fuer ein Vorleseprogramm nicht zu
            // unterscheiden.
            aria-label="Neuer Preis gültig ab"
          />
          {/*
            DAS ENDE DES BISHERIGEN PREISES IST EIN FELD, kein Automatismus.
            Vorbelegt mit dem Tag davor — das ist der Regelfall und laesst
            keine Luecke —, aber frei waehlbar: Ein Preis, der zum 30.09.
            auslaufen und erst zum 01.11. abgeloest werden soll, war vorher
            nicht auszudruecken.
          */}
          <input
            type="date"
            value={fortschreiben.ende}
            max={tagDavor(fortschreiben.ab)}
            onChange={(e) => setzeFortschreiben({ ...fortschreiben, ende: e.target.value })}
            aria-label="Bisheriger Preis gültig bis"
          />
          <span>
            <button type="submit" className="knopf knopf--haupt">
              Fortschreiben
            </button>{' '}
            <button
              type="button"
              className="knopf"
              onClick={() => setzeFortschreiben(undefined)}
            >
              Abbrechen
            </button>
          </span>
        </form>

        {/*
          DIE LÜCKENWARNUNG GEHÖRT INS FENSTER.
          Sie stand als eigener Absatz unter dem Formular. Läge sie dort, wäre
          sie hinter dem Schleier — und die einzige Stelle, an der eine
          entstehende Preislücke benannt wird, wäre nicht mehr zu sehen.
        */}
        <p
          className={
            fortschreiben.ende < tagDavor(fortschreiben.ab) ? 'warnung' : 'feld__hilfe'
          }
        >
          {fortschreiben.ende >= fortschreiben.ab ? (
            <>
              Der bisherige Preis darf spätestens am Tag vor dem neuen enden — zwei Preise für
              denselben Tag sind nicht zulässig.
            </>
          ) : fortschreiben.ende < tagDavor(fortschreiben.ab) ? (
            <>
              <strong>Lücke:</strong> Zwischen {fortschreiben.ende} und {fortschreiben.ab} gilt
              kein Preis. Die Preisauskunft antwortet in dieser Zeit „kein Preis“, und der
              Artikel lässt sich nicht in ein Angebot nehmen. Das ist zulässig — aber es sollte
              gewollt sein.
            </>
          ) : (
            <>Lückenloser Anschluss: {fortschreiben.ende} → {fortschreiben.ab}. Rückwirkend geht es nicht.</>
          )}
        </p>
        </Dialog>
      )}

      {/*
        Der Staffelsatz als GANZES (PRD-14). Eingetragen wird er als
        "ab=Preis"-Paare, weil eine Zeilenpflege zwischendurch immer einen
        Satz erzeugt, der die Monotonie verletzt — und die Preisauskunft
        rechnet in dieser Zwischenzeit. Der Server weist eine Stufe ueber dem
        Grundpreis ab: Stufe 2 ERSETZT die Preisbasis, ein hoeherer
        Staffelpreis liesse den Kunden bei groesserer Menge mehr zahlen.
      */}
      {staffel !== undefined && (
        <Dialog
          titel="Staffel"
          unterzeile={staffel.zeile}
          beiSchliessen={() => setzeStaffel(undefined)}
        >
        <form
          className="kanalzeile"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const stufen = staffel.text
              .split(',')
              .map((teil) => teil.trim())
              .filter((teil) => teil !== '')
              .map((teil) => {
                const [ab, preis] = teil.split('=');
                return { abMenge: Number(ab), einzelpreis: (preis ?? '').trim() };
              });
            void staffelSetzen(artikel.id, staffel.preis, stufen)
              .then(() => {
                setzeStaffel(undefined);
                beiAenderung();
              })
              .catch(melde);
          }}
        >
          <span className="feld__beschriftung">Staffel</span>
          <input
            type="text"
            value={staffel.text}
            onChange={(e) => setzeStaffel({ ...staffel, text: e.target.value })}
            placeholder="5=229.00, 10=209.00"
            aria-label="Staffelstufen als ab=Preis, durch Komma getrennt"
          />
          <span className="feld__hilfe">
            {/* Der Grundpreis kommt beim Speichern aus fach.preis — hier
                steht er nur zur Ansicht, damit klar ist, worauf sich die
                Stufen beziehen. */}
            zu {staffel.zeile}
          </span>
          <button type="button" className="knopf" onClick={() => setzeStaffel(undefined)}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf--haupt">
            Satz ersetzen
          </button>
        </form>
        </Dialog>
      )}

      {darfKonfigurieren && (
        <Abschnitt titel="Neuer Preis">
          <form
            className="kanalzeile"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void preisAnlegen(artikel.id, {
                preisliste: neu.liste,
                vertriebsform: neu.form,
                einzelpreis: neu.preis,
                istBrutto: false,
                gueltigAb: neu.ab,
                // Leer heisst OFFEN und nicht "heute": Ein Preis ohne Ende laeuft
                // so lange wie seine Preisliste (PRD-11).
                gueltigBis: neu.bis === '' ? null : neu.bis,
              })
                .then(() => {
                  setzeNeu({ ...neu, liste: '', preis: '', bis: '' });
                  beiAenderung();
                })
                .catch(melde);
            }}
          >
            <select
              value={neu.liste}
              onChange={(e) => setzeNeu({ ...neu, liste: e.target.value })}
              aria-label="Preisliste"
            >
              <option value="">— Preisliste —</option>
              {listen.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.kennung}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={neu.preis}
              onChange={(e) => setzeNeu({ ...neu, preis: e.target.value })}
              placeholder="249.00"
              aria-label="Einzelpreis"
            />
            <input
              type="date"
              value={neu.ab}
              onChange={(e) => setzeNeu({ ...neu, ab: e.target.value })}
              aria-label="Gültig ab"
            />
            <input
              type="date"
              value={neu.bis}
              min={neu.ab}
              onChange={(e) => setzeNeu({ ...neu, bis: e.target.value })}
              aria-label="Gültig bis, leer heißt offen"
            />
            <select
              value={neu.form}
              onChange={(e) => setzeNeu({ ...neu, form: e.target.value as Vertriebsform })}
              aria-label="Vertriebsform des Preises"
            >
              {artikel.vertriebsformen.map((f) => (
                <option key={f} value={f}>
                  {VERTRIEBSFORMNAME[f]}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="knopf"
              disabled={neu.liste === '' || neu.preis.trim() === '' || imWeg !== undefined}
            >
              Preis anlegen
            </button>
          </form>

          {/*
            PRD-11 laesst je Liste, Artikel und Vertriebsform hoechstens einen
            Preis je Zeitpunkt zu. Das ist ein Ausschlussindex, und der meldet
            sich erst beim Speichern. Die Maske kennt die vorhandenen Preise und
            sagt es VORHER — samt der beiden Wege, die aus der Lage fuehren.
          */}
          {imWeg !== undefined && (
            <p className="warnung">
              Für <strong>{imWeg.preislistenkennung} · {VERTRIEBSFORMNAME[imWeg.vertriebsform]}</strong>{' '}
              gilt bereits ein Preis von {alsEuro(imWeg.einzelpreis)} ab {imWeg.gueltigAb}
              {imWeg.gueltigBis === null ? ' ohne Ende' : ` bis ${imWeg.gueltigBis}`}. Zwei Preise
              für denselben Zeitraum sind nicht zulässig.
              {' '}Nehmen Sie <strong>fortschreiben</strong> in der Zeile darüber — das beendet den
              alten Preis am Vortag und legt den neuen an —, oder <strong>beenden</strong> Sie ihn
              zuerst.
            </p>
          )}
        </Abschnitt>
      )}
    </div>
  );
}

/** Das Ende der Preisliste, in der ein Preis steht — oder `null`. */
function endeDerListe(listen: readonly Preisliste[], preisliste: string): string | null {
  return listen.find((l) => l.id === preisliste)?.gueltigBis ?? null;
}

/**
 * Die Preisvorschau — der sichtbare Beweis der Naht (PRF-01, PRF-13).
 *
 * Dieselbe Kette, die spaeter Angebot, Auftrag und Rechnung rechnet. Drei
 * Dinge stehen hier bewusst dabei:
 *
 *   * ALLE Kandidaten, nicht nur der gewaehlte. Wer wissen will, warum ein
 *     Kunde 199 statt 249 zahlt, muss sehen, was sonst gegolten haette.
 *   * Die HERLEITUNG je Stufe. PRF-13 friert sie mit dem Beleg ein; wer sie
 *     hier nicht sieht, sieht sie nie.
 *   * Die VORBEHALTE. Ein Betrag ohne den Satz, was er nicht enthaelt, wird
 *     fuer endgueltig gehalten.
 */
function Vorschau({
  auskunft,
  menge,
  form,
  formen,
  kunde,
  kundensuche,
  kundentreffer,
  beiMenge,
  beiForm,
  beiKundensuche,
  beiKunde,
}: {
  readonly auskunft: Preisauskunft | undefined;
  readonly menge: string;
  readonly form: Vertriebsform | undefined;
  readonly formen: readonly Vertriebsform[];
  readonly kunde: { readonly id: string; readonly name: string } | null;
  readonly kundensuche: string;
  readonly kundentreffer: readonly Kundenzeile[];
  readonly beiMenge: (wert: string) => void;
  readonly beiForm: (wert: Vertriebsform) => void;
  readonly beiKundensuche: (wert: string) => void;
  readonly beiKunde: (wert: { readonly id: string; readonly name: string } | null) => void;
}): JSX.Element {
  return (
    <div className="tafel__abschnitte">
      <Abschnitt titel="Preisvorschau" zusatz={alsTag(auskunft?.stichtag ?? heute()) ?? ''}>
        <div className="kanalzeile">
          <label className="feld__beschriftung" htmlFor="vorschau-menge">
            Menge
          </label>
          <input
            id="vorschau-menge"
            type="text"
            value={menge}
            onChange={(e) => beiMenge(e.target.value)}
            inputMode="numeric"
          />
          <select
            value={form}
            onChange={(e) => beiForm(e.target.value as Vertriebsform)}
            aria-label="Vertriebsform"
          >
            {formen.map((f) => (
              <option key={f} value={f}>
                {VERTRIEBSFORMNAME[f]}
              </option>
            ))}
          </select>
          <span />
        </div>

        {/* Stufe 3 (PRD-15): Ohne Kunde ist die Auskunft der Listenpreis. */}
        <div className="kanalzeile">
          <label className="feld__beschriftung" htmlFor="vorschau-kunde">
            Kunde
          </label>
          {kunde === null ? (
            <input
              id="vorschau-kunde"
              type="text"
              value={kundensuche}
              onChange={(e) => beiKundensuche(e.target.value)}
              placeholder="Name oder Kundennummer"
              autoComplete="off"
            />
          ) : (
            <span className="kanalliste__wert">{kunde.name}</span>
          )}
          {kunde === null ? (
            <span className="feld__hilfe">ohne Kundenkondition</span>
          ) : (
            <button type="button" className="knopf knopf--eingriff knopf--klein" onClick={() => beiKunde(null)}>
              Kunde entfernen
            </button>
          )}
          <span />
        </div>

        {kunde === null && kundentreffer.length > 0 && (
          <ul className="kanalliste" aria-label="Treffer der Kundensuche">
            {kundentreffer.map((k) => (
              <li key={k.id}>
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => beiKunde({ id: k.id, name: `${k.kundennummer} · ${k.name}` })}
                >
                  {k.kundennummer} · {k.name}
                  {k.ort !== null && <span className="kanalliste__name"> · {k.ort}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}

        {auskunft === undefined && <p role="status">Wird gerechnet …</p>}

        {/* Die Antwort traegt es, die Maske sagte es nicht: Wer den Reiter
            offen hat, waehrend eine Kollegin den Artikel sperrt, sah weiter
            einen Preis fuer etwas, das in keinen neuen Beleg mehr geht. */}
        {auskunft?.waehlbar === false && (
          <p className="warnung" role="status">
            Dieser Artikel ist in neuen Belegen nicht wählbar. Der Preis gilt weiter
            für Belege, die ihn schon tragen.
          </p>
        )}

        {auskunft?.preis === null && (
          <p className="warnung" role="status">
            Für diesen Artikel gilt am {auskunft.stichtag} kein Preis. Das ist zulässig — ein
            Baustein, der nur in einem Paket vorkommt, hat keinen eigenen.
          </p>
        )}

        {auskunft?.preis != null && (
          <>
            <Feldraster>
              <Feldzeile beschriftung="Preisliste" wert={auskunft.preis.preisliste} />
              <Feldzeile beschriftung="Einzelpreis" wert={alsEuro(auskunft.preis.einzelpreis)} />
              {auskunft.preis.abMenge !== null && (
                <Feldzeile
                  beschriftung="Mengenstaffel"
                  wert={null}
                  anzeige={`ab ${String(auskunft.preis.abMenge)}: ${alsEuro(auskunft.preis.staffelpreis)}`}
                />
              )}
              <Feldzeile
                beschriftung="Steuer"
                wert={null}
                anzeige={`${auskunft.preis.steuerschluessel} · ${auskunft.preis.steuerProzent} %`}
              />
              {/* PRD-12: Welcher der beiden Werte gepflegt ist, steht dabei.
                  Ohne diese Angabe liest sich ein Bruttopreis wie ein
                  Nettopreis. */}
              <Feldzeile beschriftung="Netto" wert={alsEuro(auskunft.preis.netto)} />
              <Feldzeile beschriftung="Umsatzsteuer" wert={alsEuro(auskunft.preis.steuerbetrag)} />
              <Feldzeile
                beschriftung="Brutto"
                wert={auskunft.preis.bruttoMitSteuer}
                anzeige={
                  <>
                    <strong>{auskunft.preis.bruttoMitSteuer}</strong>
                    <span className="kanalliste__name">
                      {' '}
                      · gepflegt als {auskunft.preis.istBrutto ? 'Bruttopreis' : 'Nettopreis'}
                    </span>
                  </>
                }
              />
              {/* Der Vorteil steht NUR da, wo einer entstanden ist — eine
                  Zeile "0,00" bei jedem Listenpreis waere Lärm. */}
              {auskunft.preis.vorteil !== '0.00' && (
                <Feldzeile
                  beschriftung="Vorteil"
                  wert={auskunft.preis.vorteil}
                  anzeige={
                    <>
                      {auskunft.preis.vorteil} ({auskunft.preis.vorteilProzent} %)
                      {auskunft.preis.hoechstrabattProzent !== null && (
                        <span className="kanalliste__name">
                          {' '}
                          · Höchstrabatt {auskunft.preis.hoechstrabattProzent} %
                        </span>
                      )}
                    </>
                  }
                />
              )}
              {'deckungsbeitrag' in auskunft && (
                <Feldzeile
                  beschriftung="Deckungsbeitrag"
                  wert={null}
                  anzeige={
                    auskunft.deckungsbeitrag === null
                    || auskunft.deckungsbeitrag === undefined
                      ? '— nicht ermittelbar (Einkaufspreis nicht einsehbar)'
                      : `${alsEuro(auskunft.deckungsbeitrag.betrag)} (${alsProzent(auskunft.deckungsbeitrag.prozent)})`
                  }
                />
              )}
            </Feldraster>

            {/* PRF-11: Die Kappung wird AUSGEWIESEN und nicht still
                vorgenommen. Sie steht auch in der Herleitung darunter; hier
                steht sie noch einmal, weil eine Tabelle mit acht Zeilen sie
                nicht von selbst hervorhebt. */}
            {auskunft.preis.herleitung.some((z) => z.gekappt === true) && (
              <p className="warnung">
                Der Rabatt wurde auf den Höchstrabatt von{' '}
                {auskunft.preis.hoechstrabattProzent ?? '0.00'} % gekappt.
              </p>
            )}
          </>
        )}

        {/* RND-18: Nicht jeder Bruttobetrag ist mit jedem Steuersatz
            darstellbar. Der naechstliegende wird ausgewiesen und die
            Abweichung genannt — nicht stillschweigend gebucht. */}
        {auskunft?.preis != null && !auskunft.preis.darstellbar && (
          <p className="warnung">
            Der gepflegte Bruttobetrag ist mit diesem Steuersatz nicht genau darstellbar. Gezeigt
            ist der nächstliegende darstellbare Betrag.
          </p>
        )}

        {auskunft?.konflikt != null && (
          <p className="warnung">
            <strong>Konfigurationskonflikt.</strong> {auskunft.konflikt.grund} Betroffen:{' '}
            {auskunft.konflikt.kennungen.join(', ')}
          </p>
        )}
      </Abschnitt>

      {/* PRF-13: die Herleitung je Stufe — wer sie hier nicht sieht, sieht sie nie. */}
      {auskunft?.preis != null && (
        <Abschnitt titel="Herleitung" zusatz={`${String(auskunft.preis.herleitung.length)} Stufen`}>
          <div className="tafel--weit">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Stufe</th>
                  <th scope="col">Regel</th>
                  <th scope="col">davor</th>
                  <th scope="col">danach</th>
                </tr>
              </thead>
              <tbody>
                {auskunft.preis.herleitung.map((z, i) => (
                  <tr key={`${z.stufe}-${z.regelkennung}-${String(i)}`}>
                    <td>{z.stufe}</td>
                    <td>
                      {z.regelart}
                      <span className="kanalliste__name"> · {z.regelkennung}</span>
                    </td>
                    <td className="zahl">{z.wertDavor}</td>
                    <td className="zahl">{z.wertDanach}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Abschnitt>
      )}

      <Abschnitt titel="Kandidaten" zusatz={auskunft?.kandidaten.length ?? 0}>
        <ul className="kanalliste" aria-label="Kandidaten">
          {(auskunft?.kandidaten ?? []).length === 0 && (
            <li className="feld__hilfe">Keine Preisliste gilt am Stichtag.</li>
          )}
          {(auskunft?.kandidaten ?? []).map((k) => (
            <li key={k.kennung}>
              {/* NICHT abschneiden: "kundenindividuell" und "kundengruppe"
                  waeren nach sechs Zeichen beide "kunden". */}
              <span className="kanalliste__art">{RANGSTUFENNAME[k.rangstufe]}</span>
              <span className="kanalliste__wert">
                {k.kennung} · {alsEuro(k.einzelpreis)}
              </span>
              {k.gewaehlt && <span className="marke">gilt</span>}
            </li>
          ))}
        </ul>
      </Abschnitt>

      {/* Stufe 3 (PRD-15): Was gegriffen hat, in der Reihenfolge, in der
          es gewirkt hat. */}
      <Abschnitt titel="Konditionen" zusatz="Stufe 3">
        <ul className="kanalliste" aria-label="Konditionen">
          {(auskunft?.konditionen ?? []).length === 0 && (
            <li className="feld__hilfe">
              {kunde === null
                ? 'Kein Kunde gewählt — es gilt der Listenpreis.'
                : 'Für diesen Kunden gilt am Stichtag keine Kondition.'}
            </li>
          )}
          {(auskunft?.konditionen ?? []).map((k) => (
            <li key={k.kennung}>
              <span className="kanalliste__art">{BEREICHSNAME[k.bereich]}</span>
              <span className="kanalliste__wert">
                {k.kennung} · {k.art === 'prozent' ? `${k.wert} %` : k.wert}
                <span className="kanalliste__name"> · {k.bezeichnung}</span>
              </span>
              {k.nichtKombinierbar && <span className="marke">nicht kombinierbar</span>}
            </li>
          ))}
        </ul>
      </Abschnitt>

      <Abschnitt titel="Was hier nicht enthalten ist">
        <ul className="hilfe">
          {(auskunft?.vorbehalte ?? []).map((v) => (
            <li key={v}>{v}</li>
          ))}
        </ul>
      </Abschnitt>
    </div>
  );
}

/**
 * Der Reiter „Programm" (PRD-25, PRD-31, PRD-32).
 *
 * DREI DINGE, DIE ZUSAMMENGEHOEREN, WEIL SIE DIESELBE FRAGE BEANTWORTEN: Was
 * ist das fuer ein Produkt — nicht, was es kostet. Sie haengen deshalb an
 * `artikel/aendern` und nicht an `artikel/konfigurieren` (SYS-10).
 *
 * DIE HERKUNFT DER VORAUSSETZUNGEN STEHT DABEI. Ein geerbter Wert sieht sonst
 * aus wie ein gepflegter, und beim naechsten Gesetzesstand aendert jemand den
 * falschen — dieselbe Ueberlegung wie bei der Warengruppenvererbung (PRD-02).
 */
function Programm({
  artikel,
  darfAendern,
  beiAenderung,
  beiFehler,
  beiOeffnen,
}: {
  readonly artikel: Artikel;
  readonly darfAendern: boolean;
  readonly beiAenderung: () => void;
  readonly beiFehler: (fehler: ApiFehler) => void;
  readonly beiOeffnen?: (id: string) => void;
}): JSX.Element {
  const [fahrzeugart, setzeFahrzeugart] = useState<Fahrzeugart | ''>('');
  const [umfang, setzeUmfang] = useState<Umfang | ''>('');
  const [zielgruppe, setzeZielgruppe] = useState<Zielgruppe | ''>('');

  const v = artikel.voraussetzungen;
  const [alter, setzeAlter] = useState(v?.mindestalter === null ? '' : String(v?.mindestalter ?? ''));
  const [klassen, setzeKlassen] = useState((v?.fahrerlaubnisklassen ?? []).join(', '));
  const [kenntnisse, setzeKenntnisse] = useState(v?.vorkenntnisse ?? '');
  const [fahrzeug, setzeFahrzeug] = useState(v?.fahrzeugMitbringen ?? false);
  const [fahrzeugtext, setzeFahrzeugtext] = useState(v?.fahrzeugHinweis ?? '');
  /* CRM-104: „Eigene entfernen" fragt im Pop-up nach. */
  const [eigeneEntfernen, setzeEigeneEntfernen] = useState(false);

  async function fuehreAus(was: () => Promise<unknown>): Promise<void> {
    try {
      await was();
      beiAenderung();
    } catch (ursache) {
      beiFehler(ursache as ApiFehler);
    }
  }

  return (
    <>
      {/* PRD-32 */}
      <Abschnitt titel="Teilnahmevoraussetzungen">

        {v !== null && (
          <p className="feld__hilfe">
            {v.herkunft === 'stammprodukt'
              ? `Geerbt vom Stammprodukt ${artikel.stammprodukt ?? ''}. Was Sie hier speichern, gilt nur für diese Variante — und ersetzt die geerbten Angaben vollständig.`
              : 'An diesem Artikel gepflegt.'}
          </p>
        )}
        {/* BUC-26, BUC-126 */}
        {v === null && (
          <p className="feld__hilfe">
            Noch keine Voraussetzungen gepflegt. Sie werden im Buchungsvorgang ausgegeben und
            bestätigt.
          </p>
        )}

        <div className="feld">
          <label className="feld__beschriftung" htmlFor="prg-alter">
            Mindestalter
          </label>
          <input
            id="prg-alter"
            type="text"
            inputMode="numeric"
            value={alter}
            disabled={!darfAendern}
            onChange={(e) => setzeAlter(e.target.value)}
          />
        </div>

        <div className="feld">
          <label className="feld__beschriftung" htmlFor="prg-klassen">
            Fahrerlaubnisklassen
          </label>
          <input
            id="prg-klassen"
            type="text"
            value={klassen}
            disabled={!darfAendern}
            onChange={(e) => setzeKlassen(e.target.value)}
            placeholder="B, BE"
          />
          <p className="feld__hilfe">
            Mehrere durch Komma getrennt. Bewusst ohne Auswahlliste: Die Klassen stehen in der
            Fahrerlaubnis-Verordnung und ändern sich ohne unser Zutun.
          </p>
        </div>

        <div className="feld">
          <label className="feld__beschriftung" htmlFor="prg-kenntnisse">
            Vorkenntnisse
          </label>
          <textarea
            id="prg-kenntnisse"
            rows={2}
            value={kenntnisse}
            disabled={!darfAendern}
            onChange={(e) => setzeKenntnisse(e.target.value)}
          />
        </div>

        <div className="feld">
          <label className="liste__schalter">
            <input
              type="checkbox"
              checked={fahrzeug}
              disabled={!darfAendern}
              onChange={(e) => setzeFahrzeug(e.target.checked)}
            />
            Fahrzeug mitbringen
          </label>
          {fahrzeug && (
            <input
              type="text"
              value={fahrzeugtext}
              disabled={!darfAendern}
              onChange={(e) => setzeFahrzeugtext(e.target.value)}
              placeholder="Eigenes Fahrzeug, verkehrssicher"
              aria-label="Hinweis zum Fahrzeug"
            />
          )}
        </div>

        {darfAendern && (
          <div className="knopfreihe">
            {v?.herkunft === 'artikel' && artikel.varianteVon !== null && (
              <button
                type="button"
                className="knopf knopf--eingriff"
                onClick={() => setzeEigeneEntfernen(true)}
              >
                Eigene entfernen …
              </button>
            )}
            <button
              type="button"
              className="knopf knopf--haupt"
              onClick={() =>
                void fuehreAus(() =>
                  voraussetzungenSetzen(artikel.id, {
                    mindestalter: alter === '' ? null : Number(alter),
                    fahrerlaubnisklassen: klassen
                      .split(',')
                      .map((k) => k.trim().toUpperCase())
                      .filter((k) => k !== ''),
                    vorkenntnisse: kenntnisse === '' ? null : kenntnisse,
                    fahrzeugMitbringen: fahrzeug,
                    fahrzeugHinweis: fahrzeug && fahrzeugtext !== '' ? fahrzeugtext : null,
                  }),
                )
              }
            >
              Voraussetzungen speichern
            </button>
          </div>
        )}

        {eigeneEntfernen && (
          <Rueckfrage
            frage="Eigene Voraussetzungen entfernen?"
            unterzeile={artikel.bezeichnung}
            tat="Entfernen"
            beiBestaetigen={async () => {
              // Wirft: Der Fehler steht im Pop-up, nicht über `beiFehler` auf der Seite.
              await voraussetzungenLoeschen(artikel.id);
              setzeEigeneEntfernen(false);
              beiAenderung();
            }}
            beiSchliessen={() => setzeEigeneEntfernen(false)}
          >
            {/* PRD-32: Ohne eigene Angaben erbt die Variante vom Stammprodukt. */}
            <p>
              Die an dieser Variante gepflegten Angaben werden gelöscht; danach gelten die des
              Stammprodukts {artikel.stammprodukt ?? ''}. Wer sie wieder braucht, trägt sie neu ein.
            </p>
          </Rueckfrage>
        )}
      </Abschnitt>

      {/* PRD-31 */}
      <Abschnitt
        titel="Varianten"
        {...(artikel.varianteVon === null ? { zusatz: artikel.varianten.length } : {})}
      >

        {artikel.varianteVon !== null ? (
          <p className="hinweiszeile">
            Dieser Artikel ist eine Variante von {artikel.stammprodukt}
            {' — '}
            {[
              artikel.fahrzeugart === null ? null : FAHRZEUGARTNAME[artikel.fahrzeugart],
              artikel.umfang === null ? null : UMFANGNAME[artikel.umfang],
              artikel.zielgruppe === null ? null : ZIELGRUPPENNAME[artikel.zielgruppe],
            ]
              .filter((t) => t !== null)
              .join(', ')}.
          </p>
        ) : (
          <>
            <ul className="kanalliste">
              {artikel.varianten.length === 0 && (
                <li className="feld__hilfe">Keine Varianten.</li>
              )}
              {artikel.varianten.map((k) => (
                <li key={k.id}>
                  <span className="kanalliste__art">
                    {[
                      k.fahrzeugart === null ? null : FAHRZEUGARTNAME[k.fahrzeugart],
                      k.umfang === null ? null : UMFANGNAME[k.umfang],
                      k.zielgruppe === null ? null : ZIELGRUPPENNAME[k.zielgruppe],
                    ]
                      .filter((t) => t !== null)
                      .join(' · ')}
                  </span>
                  <span className="kanalliste__wert">
                    {beiOeffnen === undefined ? (
                      `${k.artikelnummer} · ${k.bezeichnung}`
                    ) : (
                      <button
                        type="button"
                        className="liste__oeffnen"
                        onClick={() => beiOeffnen(k.id)}
                      >
                        {k.artikelnummer} · {k.bezeichnung}
                      </button>
                    )}
                  </span>
                  {k.geloescht && <span className="marke marke--gesperrt">gelöscht</span>}
                </li>
              ))}
            </ul>

            {darfAendern && (
              <div className="kanalzeile">
                <select
                  value={fahrzeugart}
                  onChange={(e) => setzeFahrzeugart(e.target.value as Fahrzeugart | '')}
                  aria-label="Fahrzeugart"
                >
                  <option value="">Fahrzeugart …</option>
                  {(Object.keys(FAHRZEUGARTNAME) as Fahrzeugart[]).map((f) => (
                    <option key={f} value={f}>
                      {FAHRZEUGARTNAME[f]}
                    </option>
                  ))}
                </select>
                <select
                  value={umfang}
                  onChange={(e) => setzeUmfang(e.target.value as Umfang | '')}
                  aria-label="Umfang"
                >
                  <option value="">Umfang …</option>
                  {(Object.keys(UMFANGNAME) as Umfang[]).map((u) => (
                    <option key={u} value={u}>
                      {UMFANGNAME[u]}
                    </option>
                  ))}
                </select>
                <select
                  value={zielgruppe}
                  onChange={(e) => setzeZielgruppe(e.target.value as Zielgruppe | '')}
                  aria-label="Zielgruppe"
                >
                  <option value="">Zielgruppe …</option>
                  {(Object.keys(ZIELGRUPPENNAME) as Zielgruppe[]).map((z) => (
                    <option key={z} value={z}>
                      {ZIELGRUPPENNAME[z]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="knopf knopf--haupt"
                  disabled={fahrzeugart === '' && umfang === '' && zielgruppe === ''}
                  onClick={() =>
                    void fuehreAus(async () => {
                      await varianteAnlegen(artikel.id, {
                        bezeichnung: null,
                        fahrzeugart: fahrzeugart === '' ? null : fahrzeugart,
                        umfang: umfang === '' ? null : umfang,
                        zielgruppe: zielgruppe === '' ? null : zielgruppe,
                      });
                      setzeFahrzeugart('');
                      setzeUmfang('');
                      setzeZielgruppe('');
                    })
                  }
                >
                  Variante anlegen
                </button>
              </div>
            )}
            <p className="feld__hilfe">
              Jede Variante bekommt eine eigene Artikelnummer und einen eigenen Preis.
              Was sie nicht selbst mitbringt, übernimmt sie vom Stammprodukt.
            </p>
          </>
        )}

      </Abschnitt>

      {/* PRD-25: nur bei Verpflegungsprodukten — die Datenbank lässt an
          jeder anderen Produktart nichts anderes zu. */}
      {artikel.verpflegung !== null && (
        <Abschnitt titel="Verpflegung">
          <Feldraster>
            <Feldzeile beschriftung="Portionen" wert={String(artikel.verpflegung.portionen)} />
            <Feldzeile
              beschriftung="Vorlauf"
              wert={null}
              anzeige={`${String(artikel.verpflegung.vorlaufStunden)} Stunden`}
            />
            <Feldzeile
              beschriftung="Bistro"
              wert={null}
              anzeige={
                <>
                  {artikel.verpflegung.bistroName ?? '— ohne Kapazitätsbindung'}
                  {artikel.verpflegung.plaetzeJePortion !== null
                    && ` · ${String(artikel.verpflegung.plaetzeJePortion)} Plätze je Portion`}
                </>
              }
            />
          </Feldraster>
          <ul className="kanalliste">
            {artikel.verpflegung.auswahl.map((a) => (
              <li key={a.id}>
                <span className="kanalliste__wert">{a.bezeichnung}</span>
                {a.hinweis !== null && <span className="kanalliste__name"> · {a.hinweis}</span>}
                {a.istVorgabe && <span className="marke">Vorgabe</span>}
              </li>
            ))}
          </ul>
        </Abschnitt>
      )}
    </>
  );
}
